'use client';

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';

import type { AutoPopupKey } from './auto-popup-key';

/** 自動彈窗協調器的 Context 型別 */
export interface AutoPopupHandlerContextType {
	/** 各彈窗 key 的註冊狀態（true 表示該彈窗想要顯示） */
	registrationKeyValues: Record<number, boolean>;
	/** 依優先順序決定後，目前唯一可顯示的彈窗 key；無則為 null */
	activeKey: number | null;
	/** 更新指定 key 的註冊狀態 */
	setRegistrationKeyValues: (key: number, value: boolean) => void;
}

export interface AutoPopupHandlerProviderProps {
	children: ReactNode;
	/** 彈窗優先順序；未列出的 key 會排在後面，依註冊順序檢查 */
	orderConfig?: number[];
}

export interface UseRegisterAutoPopupOptions {
	/** 彈窗的唯一識別 key */
	key: AutoPopupKey;
	/** 該彈窗是否應參與自動顯示（例如條件成立時為 true） */
	isEnabled: boolean;
}

const AutoPopupHandlerContext = createContext<AutoPopupHandlerContextType | null>(null);

/**
 * 組合完整的 key 檢查順序：
 * 1. orderConfig 中定義的 key（依設定順序）
 * 2. 其餘已註冊但未在 orderConfig 中的 key
 */
function buildOrderedKeys(
	orderConfig: number[] | undefined,
	registrationKeyValues: Record<number, boolean>,
): number[] {
	const configuredOrder = orderConfig ?? [];
	const configuredKeySet = new Set(configuredOrder);
	const unconfiguredKeys = Object.keys(registrationKeyValues)
		.map(Number)
		.sort((a, b) => b - a)
		.filter((key: number) => !configuredKeySet.has(key));

	return [...configuredOrder, ...unconfiguredKeys];
}

/** 依順序找出第一個已註冊且想要顯示的 key，作為當前 activeKey */
function resolveActiveKey(
	orderedKeys: number[],
	registrationKeyValues: Record<number, boolean>,
): number | null {
	for (const key of orderedKeys) {
		if (registrationKeyValues[key]) {
			return key;
		}
	}
	return null;
}

/** 提供自動彈窗協調能力，確保同一時間只有一個彈窗顯示 */
export function AutoPopupHandlerProvider({
	children,
	orderConfig,
}: AutoPopupHandlerProviderProps) {
	const [registrationKeyValues, setRegistrationKeyValuesState] = useState<Record<number, boolean>>({});

	const setRegistrationKeyValues = useCallback((key: number, value: boolean) => {
		setRegistrationKeyValuesState((prev) => {
			// 值未變時略過更新，避免不必要的 re-render
			if (prev[key] === value) {
				return prev;
			}
			return { ...prev, [key]: value };
		});
	}, []);

	const activeKey = useMemo(
		() => resolveActiveKey(
			buildOrderedKeys(orderConfig, registrationKeyValues),
			registrationKeyValues,
		),
		[orderConfig, registrationKeyValues],
	);
	const contextValue = useMemo<AutoPopupHandlerContextType>(
		() => ({
			activeKey,
			registrationKeyValues,
			setRegistrationKeyValues,
		}),
		[activeKey, registrationKeyValues, setRegistrationKeyValues],
	);

	return (
		<AutoPopupHandlerContext.Provider value={contextValue}>
			{children}
		</AutoPopupHandlerContext.Provider>
	);
}

/** 取得 Context；若不在 Provider 內則回傳 null */
export function useOptionalAutoPopupHandler(): AutoPopupHandlerContextType | null {
	return useContext(AutoPopupHandlerContext);
}

/**
 * 註冊彈窗並回傳是否應顯示。
 * 同一時間僅 activeKey 對應的彈窗可顯示；未包在 Provider 時直接依 isEnabled 判斷。
 */
export function useRegisterAutoPopup({ key, isEnabled }: UseRegisterAutoPopupOptions): boolean {
	const api = useOptionalAutoPopupHandler();
	const setRegistrationKeyValues = api?.setRegistrationKeyValues;

	useEffect(() => {
		if (!setRegistrationKeyValues) {
			return;
		}

		setRegistrationKeyValues(key, isEnabled);

		// 元件卸載時取消註冊，避免殘留 key 佔用優先權
		return () => {
			setRegistrationKeyValues(key, false);
		};
	}, [setRegistrationKeyValues, key, isEnabled]);

	return useMemo(() => {
		if (!api) {
			return isEnabled;
		}

		return (
			isEnabled
			&& Boolean(api.registrationKeyValues[key])
			&& api.activeKey === key
		);
	}, [api, key, isEnabled]);
}

/** Alias for {@link useRegisterAutoPopup} */
export function useAutoPopupGate(key: AutoPopupKey, isEnabled: boolean): boolean {
	return useRegisterAutoPopup({ key, isEnabled });
}
